import React, { useRef } from 'react';
import { observer } from 'mobx-react';
import {
  VisitLocationDtoModelType,
} from '../../../store/mst/models';
import MapBase, { MapsRef } from '../../shared/MapBase';
import MapControlBar from '../../shared/MapBase/MapControlBar';
import toolbarButtons from './ToolBarButtons';
import {
  OrderAttemptLocation,
  getCursor,
  getOrderAttemptIconLayer,
  getLocationIconLayer,
  handleOnClear,
} from './mapHelperFunctions';
import useMapResizeObserver from './hooks/useMapResizeObserver';
import useLocalMapStore, { LocalMapStoreProps } from './hooks/useLocalMapStore';

export const MAP_DEFAULT_ZOOM = 10;

export const DEFAULT_VIEW_STATE_LOCATION = {
  latitude: 49.28455291120851,
  longitude: -123.12840882268065,
};

export const SUGGESTION_MODAL_MAP_DIMENSIONS = {
  height: 240,
  width: '100%',
};

export enum PinDropMode {
  EDITING = 'editing',
  VIEWING = 'viewing',
}


interface Props extends Omit<LocalMapStoreProps, 'mapRef'> {
  hoveredLocation?: VisitLocationDtoModelType;
  isDraggable?: boolean;
  orderAttemptLocations?: OrderAttemptLocation[];
  bordered?: boolean;
  roundedCorners?: boolean;
}

const OrdersLocationMap: React.FC<Props> = ({
  locations,
  selectedLocation,
  hoveredLocation,
  hoverLocation,
  selectLocation,
  geolocation,
  handlePinDrop,
  pinDropMode = PinDropMode.VIEWING,
  setPinDropMode,
  isDraggable = true,
  orderAttemptLocations,
  bordered = false,
  roundedCorners = true,
}) => {
  const allowPinDrop = !!handlePinDrop;
  const mapRef = useRef<MapsRef>(null);
  const mapStoreProps = {
    mapRef,
    selectedLocation,
    locations,
    geolocation,
    pinDropMode,
    handlePinDrop,
    hoverLocation,
    selectLocation,
    setPinDropMode,
    orderAttemptLocations,
  };

  const {
    reactMapGLRef,
    localPinMode,
    updatePinDropMode,
    mapViewState,
    handleViewStateChange,
    setPinDropCoordinates,
    zoomIn,
    zoomOut,
    handleLocationHover,
    handleLocationClick,
  } = useLocalMapStore(mapStoreProps);
  useMapResizeObserver(reactMapGLRef);

  const locationIconLayer = getLocationIconLayer(
    locations,
    handleLocationClick,
    handleLocationHover,
    mapViewState,
    hoveredLocation,
    selectedLocation,
  );


  const orderAttemptIconLayer = getOrderAttemptIconLayer(orderAttemptLocations);
  const layers = orderAttemptIconLayer
    ? [locationIconLayer, orderAttemptIconLayer]
    : [locationIconLayer];

  return (
    <MapBase
      ref={mapRef}
      layers={layers}
      isDraggable={isDraggable}
      bordered={bordered}
      roundedCorners={roundedCorners}
      viewState={mapViewState}
      onMove={handleViewStateChange}
      hoveredLocation={hoveredLocation}
      onClick={setPinDropCoordinates}
      getCursor={() => getCursor(localPinMode, hoveredLocation)}
      isPinDropMode={localPinMode === PinDropMode.EDITING}
      onClear={() => handleOnClear(allowPinDrop, updatePinDropMode, localPinMode)}
    >
      <MapControlBar
        dataCy="suggestion-map-toolbar"
        buttons={toolbarButtons(zoomIn, zoomOut, updatePinDropMode, allowPinDrop, localPinMode)}
      />
    </MapBase>
  );
};

export default observer(OrdersLocationMap);
